import React from 'react';
import { FaqItem, FictionalIcon } from '../../customFrontend/parts';
import { ChatappCity } from '../../siteBrands/chatapp/types';

/**
 * The city pages' copy, standard Dutch with informal "je". Each city is
 * written for that city (never a template with the name swapped: search
 * engines treat those as doorway pages). Local flavour comes from places and
 * facts, not from dialect.
 */
export type CityContent = ChatappCity;

const disclosure = (city: string): FaqItem => ({
  q: `Zijn de profielen in ${city} echte mensen die ik kan ontmoeten?`,
  a: (
    <p>
      Nee. Chatapp.nl gebruikt fictieve profielen die als doel hebben om met gebruikers te flirten. Je herkent ze
      aan het persoonsicoon <FictionalIcon language="nl" /> op hun profiel, en fysiek contact met deze profielen is
      niet mogelijk. Chatapp.nl is chat voor je plezier, geen manier om afspraakjes te maken.
    </p>
  ),
});

export const CITIES: Record<string, CityContent> = {
  '/amsterdam': {
    path: '/amsterdam',
    slug: 'amsterdam',
    name: 'Amsterdam',
    province: 'Noord-Holland',
    kicker: 'Hoofdstad aan het IJ',
    h1: 'Sexchat Amsterdam: flirten tussen grachten en pont',
    lead: 'Gratis aanmelden, je eerste berichten gratis en een chat die net zo laat doorgaat als de stad zelf.',
    intro: (
      <p>
        Amsterdam telt ruim 900.000 inwoners en elke avond lijkt de halve stad op de fiets te zitten. Tussen de
        grachten, de kroegen en de pont over het IJ is er altijd wel een moment waarop je zin hebt in een flirt.
        Op Chatapp.nl maak je gratis een profiel aan, stuur je je eerste berichten zonder te betalen en bepaal je
        zelf hoe spannend het wordt.
      </p>
    ),
    sections: [
      {
        title: 'Van de Jordaan tot De Pijp',
        body: (
          <>
            <p>
              Een Amsterdamse avond begint vaak op een bankje aan de gracht in de Jordaan, of op een terras aan de
              Albert Cuypstraat als de marktkramen net zijn opgeruimd. Wie langer doorgaat, belandt op het
              Leidseplein of het Rembrandtplein, waar de clubs pas na middernacht echt vollopen. En aan de overkant
              van het IJ trekt de NDSM-werf in Noord een eigen publiek, met festivals en loodsen vol kunst.
            </p>
            <p>
              Juist op de momenten daartussen pakken veel Amsterdammers hun telefoon. Een berichtje op de pont
              vanaf Centraal, een plagerig antwoord in de Noord/Zuidlijn, een langer gesprek als je eindelijk thuis
              op de bank zit.
            </p>
          </>
        ),
      },
      {
        title: 'Een stad vol mensen, een chat voor jou alleen',
        body: (
          <p>
            In een stad waar je overal toeristen, collega’s en buren tegenkomt, is een beetje privacy fijn. Op
            Chatapp.nl chat je onder een nickname. Niemand ziet je naam of je e-mailadres, er verschijnt niets op je
            sociale media en je kiest zelf welke meldingen je per mail krijgt. Zo blijft een flirt tussen jou en je
            scherm, of je nu op de Zuidas werkt of in Oost woont.
          </p>
        ),
      },
      {
        title: 'Wanneer Amsterdam chat',
        body: (
          <p>
            Doordeweeks is het druk na het werk, als de fietsenstallingen leeglopen en iedereen op weg naar huis is.
            In het weekend schuift alles op: een late chat na het stappen, of een luie zondag in het Vondelpark met
            je telefoon in de hand. Omdat Chatapp.nl gewoon in je browser werkt, heb je geen app nodig. Je opent de
            site en gaat verder waar je gebleven was.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Wat kost sexchat in Amsterdam op Chatapp.nl?',
        a: <p>Aanmelden is gratis en je eerste berichten zijn gratis. Daarna kosten berichten, flirts en foto’s credits. Je koopt alleen credits als je verder wilt chatten, en je ziet elke prijs voordat je betaalt.</p>,
      },
      {
        q: 'Heb ik een creditcard nodig om me aan te melden?',
        a: <p>Nee. Bij het aanmelden vragen we nooit om een creditcard of andere betaalgegevens. Je kiest een nickname en kunt meteen beginnen.</p>,
      },
      disclosure('Amsterdam'),
    ],
    cta: {
      title: 'Je eerste berichten zijn gratis',
      text: 'Maak in een minuut een profiel aan en begin vanavond nog aan een flirt, waar je ook bent in Amsterdam.',
      button: 'Gratis aanmelden',
    },
  },

  '/rotterdam': {
    path: '/rotterdam',
    slug: 'rotterdam',
    name: 'Rotterdam',
    province: 'Zuid-Holland',
    kicker: 'Havenstad aan de Maas',
    h1: 'Sexchat Rotterdam: flirtchat met uitzicht op de Maas',
    lead: 'Een gratis profiel, gratis eerste berichten en chatten in het tempo van een stad die nooit stilstaat.',
    intro: (
      <p>
        Rotterdam is de stad van de skyline, de grootste haven van Europa en een tempo dat je nergens anders in
        Nederland vindt. Na het bombardement van 1940 is het centrum opnieuw opgebouwd, en dat zie je nog steeds:
        hier wordt niet stilgezeten. Op Chatapp.nl zet je in een minuut een profiel neer en begin je gratis aan
        je eerste flirt.
      </p>
    ),
    sections: [
      {
        title: 'Van de Markthal tot Katendrecht',
        body: (
          <>
            <p>
              Een avond in Rotterdam kan beginnen onder het kleurrijke plafond van de Markthal, met daarna een
              drankje aan de Oude Haven bij de kubuswoningen. Over de Erasmusbrug fiets je naar de Kop van Zuid,
              waar hotel New York aan de kade ligt. Op Katendrecht zitten de restaurants aan het Deliplein dicht op
              elkaar, en wie nog niet naar huis wil, gaat terug naar de Meent of de clubs rond het Stadhuisplein.
            </p>
            <p>
              Het is een stad van grote afstanden en snelle verbindingen. Veel Rotterdammers chatten in de metro
              naar Zuid, op de watertaxi over de Maas of in de trein vanaf Centraal.
            </p>
          </>
        ),
      },
      {
        title: 'Direct, zonder omwegen',
        body: (
          <p>
            Rotterdam staat bekend om zijn directheid, en dat past bij hoe Chatapp.nl werkt. Je hoeft geen lang
            profiel te schrijven of eerst door een vragenlijst. Kies een nickname, kijk rond en stuur een bericht.
            Je eerste berichten zijn gratis, dus je merkt meteen of de chat bevalt. Daarna gebruik je credits voor
            berichten, flirts en foto’s, en je ziet altijd vooraf wat iets kost.
          </p>
        ),
      },
      {
        title: 'Na de wedstrijd, na de dienst',
        body: (
          <p>
            In een havenstad werken veel mensen in ploegen, en dat merk je aan de chat: er is ’s nachts net zo
            goed iemand online als ’s avonds. Op wedstrijddagen van Feyenoord is het rond De Kuip even druk, en
            daarna komt de stad langzaam tot rust. Of je nu van een late dienst komt of na de wedstrijd op de bank
            neerploft, Chatapp.nl werkt gewoon in je browser, zonder app.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Kan ik op Chatapp.nl chatten zonder dat iemand het weet?',
        a: <p>Ja. Andere gebruikers zien alleen je nickname, nooit je naam of e-mailadres. Er wordt niets op sociale media geplaatst en je bepaalt zelf welke meldingen je per e-mail krijgt.</p>,
      },
      {
        q: 'Hoeveel gratis berichten krijg ik?',
        a: <p>Je eerste berichten zijn gratis, zodat je rustig kunt kijken of de chat je bevalt. Daarna kosten berichten, flirts en foto’s credits. De prijzen staan op de site voordat je iets koopt.</p>,
      },
      disclosure('Rotterdam'),
    ],
    cta: {
      title: 'Begin vandaag aan je eerste chat',
      text: 'Gratis aanmelden duurt een minuut. Je eerste berichten zijn gratis, van Noord tot Zuid.',
      button: 'Maak een gratis profiel',
    },
  },

  '/den-haag': {
    path: '/den-haag',
    slug: 'den-haag',
    name: 'Den Haag',
    province: 'Zuid-Holland',
    kicker: 'Hofstad aan zee',
    h1: 'Sexchat Den Haag: flirten tussen Plein en strand',
    lead: 'Gratis aanmelden, je eerste berichten gratis en een flirt die net zo makkelijk loopt als de tram naar zee.',
    intro: (
      <p>
        Den Haag is de stad van de regering, de koning en de internationale hoven, maar ook van het strand, de
        pier en lange zomeravonden aan zee. Het is een stad met twee gezichten: ’s ochtends in pak, ’s avonds op
        een terras met zand tussen je tenen. Op Chatapp.nl kies je zelf welk gezicht je laat zien, onder een
        nickname en met je eerste berichten gratis.
      </p>
    ),
    sections: [
      {
        title: 'Van het Plein tot Scheveningen',
        body: (
          <>
            <p>
              Na het werk lopen de terrassen op het Plein vol met ambtenaren, advocaten en diplomaten. Een stukje
              verderop is de Grote Markt de plek voor een avond met vrienden, met cafés die tot laat open zijn. In
              het Zeeheldenkwartier vind je kleine restaurants en tweedehandswinkels, en in het Statenkwartier
              statige straten met herenhuizen.
            </p>
            <p>
              Als het weer meezit, neemt half Den Haag de tram naar Scheveningen. Op de boulevard en bij de pier is
              het in de zomer tot ver na zonsondergang druk, en dat is precies het moment waarop een flirt op je
              telefoon goed past.
            </p>
          </>
        ),
      },
      {
        title: 'Discreet, zoals het hoort',
        body: (
          <p>
            In een stad waar veel mensen een functie hebben waarbij discretie telt, is privacy geen luxe. Op
            Chatapp.nl zien anderen alleen je nickname. Je naam en e-mailadres blijven bij ons, er gaat niets naar
            sociale media en meldingen per mail zet je aan of uit wanneer je wilt. Zo kun je ontspannen chatten,
            ook als je overdag in een vergaderzaal zit.
          </p>
        ),
      },
      {
        title: 'Een avond aan zee, een chat op de bank',
        body: (
          <p>
            Haagse avonden hebben een eigen ritme. In de zomer begint alles later, na een dag op het strand. In de
            winter trekt de stad zich terug in de cafés rond de Grote Markt of gewoon op de bank in Duinoord of
            Bezuidenhout. Chatapp.nl werkt in je browser, op je telefoon of laptop, dus je kunt een gesprek op de
            tram beginnen en thuis verder chatten.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Moet ik een app downloaden?',
        a: <p>Nee. Chatapp.nl werkt in de browser van je telefoon, tablet of computer. Je hoeft niets te installeren en er staat geen app op je startscherm die iemand anders kan zien.</p>,
      },
      {
        q: 'Wat kost chatten in Den Haag?',
        a: <p>Je profiel is gratis, net als je eerste berichten. Daarna kosten berichten, flirts en foto’s credits. Je koopt alleen een pakket als je dat zelf wilt, en je ziet de prijs altijd vooraf.</p>,
      },
      disclosure('Den Haag'),
    ],
    cta: {
      title: 'Van de pier naar je inbox',
      text: 'Maak gratis een profiel aan en stuur vanavond je eerste berichten, zonder dat het iets kost.',
      button: 'Gratis beginnen',
    },
  },

  '/utrecht': {
    path: '/utrecht',
    slug: 'utrecht',
    name: 'Utrecht',
    province: 'Utrecht',
    kicker: 'Stad van de Dom',
    h1: 'Sexchat Utrecht: flirten in de schaduw van de Dom',
    lead: 'Een gratis profiel, gratis eerste berichten en een flirt die past tussen college, werk en de werfkelders.',
    intro: (
      <p>
        Utrecht ligt midden in het land en voelt ook zo: iedereen komt er langs. Utrecht Centraal is het drukste
        station van Nederland, de universiteit trekt studenten uit het hele land en de Domtoren is met 112 meter
        de hoogste kerktoren van het land. Op Chatapp.nl maak je gratis een profiel en begin je aan een flirt
        zonder dat je eerste berichten iets kosten.
      </p>
    ),
    sections: [
      {
        title: 'Werfkelders, Neude en Lombok',
        body: (
          <>
            <p>
              Wat Utrecht bijzonder maakt, zijn de werfkelders langs de Oudegracht: oude opslagruimtes op het
              niveau van het water, waar nu terrassen en restaurants zitten. Op een zomeravond zit je daar bijna
              op het water, met de stad boven je hoofd. De Neude en de Voorstraat zijn de plekken voor een
              drankje, TivoliVredenburg voor een concert en Lombok voor eten uit alle hoeken van de wereld.
            </p>
            <p>
              Utrecht is een fietsstad bij uitstek. Bij het station staat de grootste fietsenstalling ter wereld,
              en op de Croeselaan en de Biltstraat zie je ’s avonds een eindeloze stroom lampjes. Veel gesprekken
              beginnen daarna, als de fiets op slot staat.
            </p>
          </>
        ),
      },
      {
        title: 'Tussen trein en tentamen',
        body: (
          <p>
            Omdat zoveel Utrechters reizen, wordt er veel gechat in de trein. Een bericht onderweg naar Amsterdam,
            een antwoord op de terugweg, een langer gesprek vanaf je bank in Wittevrouwen of Leidsche Rijn.
            Chatapp.nl werkt in je browser, dus je hebt geen app nodig en je gesprekken staan klaar op elk apparaat
            waarop je inlogt.
          </p>
        ),
      },
      {
        title: 'Zo werkt het',
        body: (
          <p>
            Je kiest een nickname, geeft aan met wie je wilt chatten en kijkt rond. Je eerste berichten zijn
            gratis. Wil je daarna verder, dan gebruik je credits voor berichten, flirts en foto’s. Je koopt alleen
            iets als je dat zelf wilt en je ziet elke prijs voordat je betaalt. Aanmelden vraagt nooit om een
            creditcard.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Is Chatapp.nl alleen voor studenten?',
        a: <p>Nee. Iedereen van 18 jaar of ouder kan een profiel aanmaken, of je nu studeert, werkt of allebei. Je moet wel 18+ zijn.</p>,
      },
      {
        q: 'Ziet iemand dat ik op Chatapp.nl zit?',
        a: <p>Alleen als je het zelf vertelt. Anderen zien je nickname, nooit je naam of e-mailadres, en er verschijnt niets op je sociale media.</p>,
      },
      disclosure('Utrecht'),
    ],
    cta: {
      title: 'Je eerste flirt in Utrecht is gratis',
      text: 'Aanmelden duurt een minuut en kost niets. Je eerste berichten zijn van ons.',
      button: 'Maak een gratis profiel',
    },
  },

  '/eindhoven': {
    path: '/eindhoven',
    slug: 'eindhoven',
    name: 'Eindhoven',
    province: 'Noord-Brabant',
    kicker: 'Lichtstad van het zuiden',
    h1: 'Sexchat Eindhoven: flirtchat in de Lichtstad',
    lead: 'Gratis aanmelden, je eerste berichten gratis en chatten in een stad die op ideeën draait.',
    intro: (
      <p>
        Eindhoven heet de Lichtstad, en dat komt door Philips, dat hier in 1891 begon met het maken van
        gloeilampen. Van een stad van fabrieken is Eindhoven uitgegroeid tot een stad van ontwerpers,
        ingenieurs en techbedrijven. Op Chatapp.nl hoef je niets uit te vinden: je maakt gratis een profiel aan en
        stuurt je eerste berichten zonder te betalen.
      </p>
    ),
    sections: [
      {
        title: 'Stratumseind, Strijp-S en GLOW',
        body: (
          <>
            <p>
              Uitgaan in Eindhoven betekent voor veel mensen Stratumseind, een van de langste kroegstraten van
              Nederland, waar het in het weekend tot diep in de nacht vol is. Wie het rustiger wil, gaat naar
              Strijp-S, het oude fabrieksterrein van Philips met restaurants, ateliers en loodsen waar van alles
              gebeurt.
            </p>
            <p>
              In november verandert de hele binnenstad tijdens GLOW in een route van lichtkunst, en in oktober
              komen tijdens de Dutch Design Week bezoekers van over de hele wereld naar de stad. Dat zijn drukke
              weken, met lange avonden en veel tijd op je telefoon tussen twee exposities door.
            </p>
          </>
        ),
      },
      {
        title: 'Voor wie lange dagen maakt',
        body: (
          <p>
            Rond de High Tech Campus en de bedrijven in de regio werken veel mensen lange dagen, vaak met collega’s
            uit het buitenland. Dan is het fijn als een flirt niet veel moeite kost. Op Chatapp.nl heb je geen lang
            profiel nodig. Je kiest een nickname en kijkt rond. Een bericht sturen kan vanuit de trein, vanaf je
            bureau in de pauze of ’s avonds thuis in Woensel of Stratum.
          </p>
        ),
      },
      {
        title: 'Helder over de kosten',
        body: (
          <p>
            Een technische stad houdt van duidelijke regels, dus hier zijn ze. Aanmelden is gratis en vraagt nooit
            om een creditcard. Je eerste berichten zijn gratis. Daarna kosten berichten, flirts en foto’s credits,
            die je koopt in pakketten waarvan je de prijs vooraf ziet. Er is geen abonnement dat ongemerkt
            doorloopt.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Werkt Chatapp.nl op mijn telefoon?',
        a: <p>Ja. Chatapp.nl is gemaakt voor de telefoon en werkt in elke moderne browser. Op een tablet of laptop werkt het net zo goed, zonder dat je een app installeert.</p>,
      },
      {
        q: 'Zit ik ergens aan vast als ik credits koop?',
        a: <p>Nee. Je koopt een pakket credits wanneer jij dat wilt. Er loopt geen abonnement door en je ziet de prijs altijd voordat je betaalt.</p>,
      },
      disclosure('Eindhoven'),
    ],
    cta: {
      title: 'Zet het licht aan op je chat',
      text: 'Maak in een minuut een gratis profiel aan. Je eerste berichten zijn gratis.',
      button: 'Gratis aanmelden',
    },
  },

  '/groningen': {
    path: '/groningen',
    slug: 'groningen',
    name: 'Groningen',
    province: 'Groningen',
    kicker: 'Stad van het noorden',
    h1: 'Sexchat Groningen: flirten tot de kroegen dichtgaan',
    lead: 'Een gratis profiel, gratis eerste berichten en een chat voor een stad waar de avond pas laat begint.',
    intro: (
      <p>
        Groningen is de grootste stad van het noorden en een van de jongste steden van het land. Bijna een kwart
        van de inwoners studeert aan de Rijksuniversiteit of de Hanzehogeschool, en dat merk je op straat, in de
        kroegen en ’s nachts op de fiets. Op Chatapp.nl maak je gratis een profiel aan en begin je aan een flirt
        zonder dat je eerste berichten iets kosten.
      </p>
    ),
    sections: [
      {
        title: 'Poelestraat, Peperstraat en de Grote Markt',
        body: (
          <>
            <p>
              Het uitgaansleven draait om de Poelestraat, de Peperstraat en de Grote Markt, waar de Martinitoren
              boven alles uitsteekt. Groningen kent in de binnenstad geen vaste sluitingstijden voor de horeca,
              dus een avond kan hier lang duren. Overdag zit de stad op de terrassen aan de Vismarkt of in het
              Noorderplantsoen, en op het dak van het Forum heb je een van de mooiste uitzichten over de stad.
            </p>
            <p>
              Groningen is compact. Je fietst in tien minuten van de Oosterpoort naar de Korrewegwijk, dus de
              afstand tussen een bericht en je bed is hier kort.
            </p>
          </>
        ),
      },
      {
        title: 'Lange winteravonden',
        body: (
          <p>
            In het noorden zijn de winters lang en de avonden donker. Dan is het verleidelijk om de stad even voor
            gezien te houden en thuis te blijven, met je telefoon en een warme deken. Precies op die avonden is
            Chatapp.nl op zijn drukst. Een flirt begint met één bericht, en omdat je eerste berichten gratis zijn,
            kost het je niets om te kijken waar het gesprek heen gaat.
          </p>
        ),
      },
      {
        title: 'Ook buiten de stad',
        body: (
          <p>
            Niet iedereen die in Groningen werkt of studeert, woont er ook. Veel mensen pendelen vanuit Haren,
            Assen of de dorpen in de provincie. Chatapp.nl werkt in je browser, dus je chat net zo makkelijk in
            de bus of de trein als thuis. Je hebt geen app nodig, en wat je typt is alleen zichtbaar voor jou en
            degene met wie je chat.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Wie kan zich aanmelden?',
        a: <p>Iedereen van 18 jaar of ouder. Chatapp.nl is een dienst voor volwassenen en je moet 18+ zijn om een profiel aan te maken.</p>,
      },
      {
        q: 'Wat kost het na de gratis berichten?',
        a: <p>Daarna kosten berichten, flirts en foto’s credits. Je koopt alleen credits als je verder wilt chatten, en elke prijs staat op de site voordat je betaalt. Aanmelden vraagt nooit om een creditcard.</p>,
      },
      disclosure('Groningen'),
    ],
    cta: {
      title: 'De avond is nog jong',
      text: 'Maak gratis een profiel aan en stuur je eerste berichten, in de stad of erbuiten.',
      button: 'Start gratis',
    },
  },

  '/tilburg': {
    path: '/tilburg',
    slug: 'tilburg',
    name: 'Tilburg',
    province: 'Noord-Brabant',
    kicker: 'Brabantse stad van wol en spoor',
    h1: 'Sexchat Tilburg: flirtchat van Spoorzone tot Piushaven',
    lead: 'Gratis aanmelden, je eerste berichten gratis en chatten op je eigen tempo.',
    intro: (
      <p>
        Tilburg was ooit de wolstad van Nederland, met tientallen textielfabrieken. Die tijd is voorbij, maar de
        stad heeft haar oude gebouwen een nieuw leven gegeven: in de Spoorzone, waar treinen werden onderhouden,
        zitten nu een bibliotheek, restaurants, ateliers en kantoren. Op Chatapp.nl begin je
        net zo makkelijk opnieuw: met een gratis profiel en gratis eerste berichten.
      </p>
    ),
    sections: [
      {
        title: 'Van de Korte Heuvel tot de Piushaven',
        body: (
          <>
            <p>
              In het centrum zijn de terrassen op de Korte Heuvel en het Pieter Vreedeplein de plekken waar een
              avond begint. Aan de Piushaven liggen boten aan de kade en zitten restaurants met uitzicht op het
              water. Poppodium 013 trekt bands uit binnen- en buitenland, en in de Spoorzone is de LocHal, de oude
              locomotievenloods, een huiskamer voor de hele stad geworden.
            </p>
            <p>
              In juli staat de Tilburgse Kermis in de binnenstad, een van de grootste kermissen van de Benelux.
              Dan is het tien dagen lang tot laat druk, met lichtjes, muziek en veel mensen op straat.
            </p>
          </>
        ),
      },
      {
        title: 'Gezellig, maar op jouw voorwaarden',
        body: (
          <p>
            Brabant staat bekend om gezelligheid, maar een flirt is iets wat je liever voor jezelf houdt. Op
            Chatapp.nl chat je onder een nickname. Je naam en e-mailadres blijven privé, er gaat niets naar je
            sociale media en je bepaalt zelf welke mails je krijgt. Je kunt dus rustig rondkijken, ook in een stad
            waar je op de markt altijd wel een bekende tegenkomt.
          </p>
        ),
      },
      {
        title: 'Een flirt na het werk',
        body: (
          <p>
            Veel Tilburgers werken in de logistiek en de zorg, vaak met wisselende diensten. Daarom is er ook op
            ongewone tijden iemand om mee te chatten. Of je nu om zes uur ’s ochtends klaar bent of om middernacht
            thuiskomt, je opent Chatapp.nl in je browser en gaat verder met je gesprek. Je eerste berichten zijn
            gratis, daarna gebruik je credits.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Kan ik op elk moment chatten?',
        a: <p>Ja. Chatapp.nl is dag en nacht open. Je kunt een gesprek beginnen wanneer het jou uitkomt en later verdergaan op een ander apparaat.</p>,
      },
      {
        q: 'Moet ik betalen om me aan te melden?',
        a: <p>Nee. Aanmelden is gratis, vraagt nooit om een creditcard en je eerste berichten zijn ook gratis. Daarna kosten berichten, flirts en foto’s credits.</p>,
      },
      disclosure('Tilburg'),
    ],
    cta: {
      title: 'Klaar voor een flirt?',
      text: 'Je profiel staat er binnen een minuut. Je eerste berichten zijn gratis, van Reeshof tot Oud-Noord.',
      button: 'Gratis aanmelden',
    },
  },

  '/almere': {
    path: '/almere',
    slug: 'almere',
    name: 'Almere',
    province: 'Flevoland',
    kicker: 'De jongste grote stad',
    h1: 'Sexchat Almere: flirtchat in de nieuwste stad van het land',
    lead: 'Een gratis profiel, gratis eerste berichten en een chat die meereist op de trein naar Amsterdam.',
    intro: (
      <p>
        Almere is de jongste grote stad van Nederland. Waar nu meer dan 200.000 mensen wonen, lag vijftig jaar
        geleden nog water: de eerste huizen werden in 1976 opgeleverd op grond die op de zee was gewonnen. Een
        nieuwe stad vraagt om nieuwe gewoontes, en een flirt op je telefoon past daar goed bij. Op Chatapp.nl
        begin je gratis.
      </p>
    ),
    sections: [
      {
        title: 'Weerwater, Haven en Poort',
        body: (
          <>
            <p>
              Almere bestaat uit stadsdelen met elk een eigen karakter. Almere Stad heeft het centrum, met winkels,
              bioscopen en cafés rond het Weerwater, waar in 2022 de Floriade werd gehouden. Almere Haven, het
              oudste deel, heeft een haventje aan het Gooimeer en voelt bijna als een dorp. In Almere Poort ligt
              een strand aan het IJmeer, met in de zomer lange avonden aan het water.
            </p>
            <p>
              Het is een ruim opgezette stad met veel groen, brede fietspaden en vrije busbanen. Je zit dus vaak
              even onderweg, en dat is een mooi moment voor een bericht.
            </p>
          </>
        ),
      },
      {
        title: 'Pendelen en chatten',
        body: (
          <p>
            Veel Almeerders werken in Amsterdam, Utrecht of het Gooi en reizen elke dag met de trein of over de
            A6. Die twintig minuten in de trein zijn voor veel mensen het moment om bij te praten, of om een flirt
            een beetje spannender te maken. Chatapp.nl werkt in je browser, dus je kunt een gesprek in de trein
            beginnen en ’s avonds thuis in Buiten of Stad verdergaan.
          </p>
        ),
      },
      {
        title: 'Rustig thuis, toch een spannende avond',
        body: (
          <p>
            Almere is een stad van gezinnen en rustige straten, en de avonden zijn er stiller dan in Amsterdam.
            Dat hoeft niet saai te zijn. Op Chatapp.nl kies je een nickname, kijk je rond en stuur je een
            bericht wanneer je zin hebt. Je eerste berichten zijn gratis. Daarna kosten berichten, flirts en
            foto’s credits, en je ziet altijd vooraf wat iets kost.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Blijft mijn chat privé?',
        a: <p>Ja. Anderen zien alleen je nickname, nooit je naam, adres of e-mailadres. Er gaat niets naar sociale media en je kiest zelf welke meldingen je per mail krijgt.</p>,
      },
      {
        q: 'Hoe meld ik me aan?',
        a: <p>Je kiest met wie je wilt chatten, bedenkt een nickname en vult je e-mailadres in. Dat duurt ongeveer een minuut, kost niets en vraagt nooit om een creditcard.</p>,
      },
      disclosure('Almere'),
    ],
    cta: {
      title: 'Een nieuwe stad, een nieuwe flirt',
      text: 'Maak gratis een profiel aan en stuur je eerste berichten, thuis of onderweg.',
      button: 'Maak een gratis profiel',
    },
  },

  '/breda': {
    path: '/breda',
    slug: 'breda',
    name: 'Breda',
    province: 'Noord-Brabant',
    kicker: 'Nassaustad in Brabant',
    h1: 'Sexchat Breda: flirten van Havermarkt tot Valkenberg',
    lead: 'Gratis aanmelden, je eerste berichten gratis en een chat met de warmte van een Brabantse binnenstad.',
    intro: (
      <p>
        Breda is een stad met een rijke geschiedenis. Het kasteel was eeuwenlang van de familie Van Nassau, de
        voorouders van het koningshuis, en is nu de Koninklijke Militaire Academie. De Grote Kerk is van ver te
        zien, en eromheen ligt een binnenstad die gemaakt lijkt voor lange avonden. Op Chatapp.nl maak je gratis
        een profiel en begin je aan je eerste flirt.
      </p>
    ),
    sections: [
      {
        title: 'Terrassen in de schaduw van de Grote Kerk',
        body: (
          <>
            <p>
              Het hart van het Bredase uitgaansleven is de Havermarkt, een plein dat bijna helemaal uit terrassen
              bestaat, met de Grote Kerk erboven. Op de Grote Markt en in de Halstraat gaat het ’s avonds verder.
              Wie het rustiger wil, wandelt door het Valkenberg, het park tussen het station en het centrum, of
              langs het water bij het Spanjaardsgat.
            </p>
            <p>
              Met Hemelvaart staat de stad in het teken van het Breda Jazz Festival, met podia op de pleinen. En
              met carnaval verandert Breda dagenlang in één groot feest. Daarna is het heerlijk om bij te komen,
              met je telefoon en een goed gesprek.
            </p>
          </>
        ),
      },
      {
        title: 'Een flirt met stijl',
        body: (
          <p>
            Breda is een stad van goed eten, mooie winkels en verzorgde terrassen, en ook een flirt mag wel wat
            stijl hebben. Op Chatapp.nl begin je met een nickname en een eerste bericht. Je kunt plagen,
            complimenten geven en het gesprek langzaam spannender maken, helemaal in je eigen tempo. Je eerste
            berichten zijn gratis, daarna gebruik je credits voor berichten, flirts en foto’s.
          </p>
        ),
      },
      {
        title: 'Tussen Randstad en Antwerpen',
        body: (
          <p>
            Breda ligt halverwege Rotterdam en Antwerpen, en veel inwoners werken in een van beide richtingen. In
            de trein of in de pauze is er altijd even tijd voor een berichtje. Chatapp.nl werkt in je browser, op
            elk apparaat. Je hebt geen app nodig, en je gesprekken staan klaar zodra je weer inlogt.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Is aanmelden echt gratis?',
        a: <p>Ja. Je profiel aanmaken is gratis en je eerste berichten ook. Bij het aanmelden vragen we nooit om een creditcard. Pas als je verder wilt chatten, koop je credits.</p>,
      },
      {
        q: 'Kan ik foto’s sturen?',
        a: <p>Ja. Naast berichten kun je flirts en foto’s sturen. Daarvoor gebruik je credits, en je ziet altijd vooraf hoeveel iets kost.</p>,
      },
      disclosure('Breda'),
    ],
    cta: {
      title: 'Van terras naar inbox',
      text: 'Maak in een minuut een gratis profiel aan en stuur vanavond je eerste berichten.',
      button: 'Gratis beginnen',
    },
  },

  '/nijmegen': {
    path: '/nijmegen',
    slug: 'nijmegen',
    name: 'Nijmegen',
    province: 'Gelderland',
    kicker: 'De oudste stad van Nederland',
    h1: 'Sexchat Nijmegen: flirtchat aan de Waalkade',
    lead: 'Een gratis profiel, gratis eerste berichten en een chat in de oudste stad van het land.',
    intro: (
      <p>
        Nijmegen is de oudste stad van Nederland. De Romeinen bouwden hier bijna tweeduizend jaar geleden een
        nederzetting op de heuvels aan de Waal, en in 2005 vierde de stad haar tweeduizendste verjaardag. Toch
        voelt Nijmegen allesbehalve oud: met de Radboud Universiteit en de hogeschool is het een stad vol
        jonge mensen. Op Chatapp.nl begin je gratis aan een flirt.
      </p>
    ),
    sections: [
      {
        title: 'Van het Valkhof tot de Waalkade',
        body: (
          <>
            <p>
              Vanaf het Valkhofpark kijk je uit over de Waal en de bogen van de Waalbrug. Langs de Waalkade zitten
              restaurants en cafés met terrassen aan het water, en op de Grote Markt en in de Lange Hezelstraat,
              de oudste winkelstraat van Nederland, loopt de stad ’s avonds vol. Aan de overkant ligt de
              Spiegelwaal, met stranden waar het in de zomer tot laat licht is.
            </p>
            <p>
              Elk jaar in juli lopen tienduizenden wandelaars de Vierdaagse, en dan is de stad een week lang één
              groot feest tijdens de Vierdaagsefeesten. Daarna wordt het weer rustig, en dat is een mooi moment om
              op de bank je telefoon erbij te pakken.
            </p>
          </>
        ),
      },
      {
        title: 'Een stad met een open blik',
        body: (
          <p>
            Nijmegen staat bekend als een open en eigenzinnige stad, waar je mag zijn wie je bent. Dat past
            goed bij een flirtchat. Op Chatapp.nl laat je onder een nickname zien wat je wilt, en verder niets. Je naam
            en e-mailadres blijven privé, er gaat niets naar je sociale media en je kiest zelf welke meldingen je
            krijgt.
          </p>
        ),
      },
      {
        title: 'Hoe je begint',
        body: (
          <p>
            Aanmelden duurt ongeveer een minuut. Je geeft aan met wie je wilt chatten, kiest een nickname en kijkt
            rond. Je eerste berichten zijn gratis, zodat je eerst kunt proeven hoe het bevalt. Wil je daarna
            verder, dan gebruik je credits voor berichten, flirts en foto’s. Een creditcard is bij het aanmelden
            nooit nodig.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Kan ik chatten vanaf mijn laptop?',
        a: <p>Ja. Chatapp.nl werkt in de browser op je laptop, tablet en telefoon. Je gesprekken staan klaar op elk apparaat waarop je inlogt.</p>,
      },
      {
        q: 'Wat kost Chatapp.nl?',
        a: <p>Aanmelden is gratis en je eerste berichten zijn gratis. Daarna kosten berichten, flirts en foto’s credits. Je koopt alleen credits als je dat zelf wilt en je ziet de prijs altijd vooraf.</p>,
      },
      disclosure('Nijmegen'),
    ],
    cta: {
      title: 'Tweeduizend jaar geschiedenis, één nieuwe flirt',
      text: 'Maak gratis een profiel aan en stuur je eerste berichten, aan de Waal of waar je ook bent.',
      button: 'Gratis aanmelden',
    },
  },
};
